import React, { Suspense, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import queryString from 'query-string';
import { SubmenuButton, SubmenuFlyoutMenu, SubmenuLink } from './navsubmenu';
import {
  CaseStudiesContent,
  MobileDrawer,
  PartnersContent,
  PlatformContent,
  ResourcesContent,
  SolutionsContent,
} from './navcontents';
import { SiteFooter } from './sitefooter';
import { Toaster } from 'react-hot-toast';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';
import {
  convertUrltoCdn,
  getAppUrl,
  useWindowSize,
  useBannerFlag,
  getUtmSource,
} from '../utils/helpers';
import { APopover } from './apopover';
import { LogoContextMenu } from './logo-context-menu';
import { Fade as Hamburger } from 'hamburger-react';
import AlloyAutomationLogo from '../../static/images/logo/rebrand/alloy-automation.svg';
import USFlag from '../../static/images/data-center/us.svg';
import EUFlag from '../../static/images/data-center/eu.svg';
import { ALogo } from './alogo';

const LazyGdpr = React.lazy(() => import('./gdpr'));
const LazyBanner = React.lazy(() => import('./banner'));
const SiteLayout = ({
  data,
  children,
  hideMenu,
  hideFooterMenu,
  hideLoginBtn,
  metaTitle,
  metaKeywords,
  metaDescription,
  location,
  article,
  seoImage,
  pageRef,
}) => {
  const twitterUsername = '@AlloyAutomation';
  const blogSample = data?.allStrapiBlog?.edges;
  const hasBanner = data?.strapiBanner?.show_banner;
  const myUrl = process.env.MY_URL ? process.env.MY_URL : '';
  const defaultOGUrl =
    myUrl && location?.pathname ? myUrl + location.pathname : myUrl;
  const defaultOGImage = convertUrltoCdn(data.strapiHomePage.seo_image.url);

  const [loading, setLoading] = useState(true);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isAppFilterOverlayOpen, setIsAppFilterOverlayOpen] = useState('');
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const logoRef = useRef(null);
  const closeTimeout = useRef(null);
  const screenSize = useWindowSize();
  const flag = useBannerFlag();

  useEffect(() => {
    // On page transition
    setTimeout(() => setLoading(false), 10);

    // Track UTM source parameters
    if (typeof window !== 'undefined') {
      const parsed = queryString.parse(window?.location?.search);
      const utm_source = parsed?.utm_source;
      if (utm_source) {
        localStorage.setItem('utm_source', utm_source);
      }
    }
  }, []);

  useEffect(() => {
    // Listen app filter on apps page if opened/closes
    function checkAppFilterFlag() {
      const flag = localStorage.getItem('hasFilterOverlay');
      setIsAppFilterOverlayOpen(JSON.parse(flag));
    }

    window.addEventListener('storage', checkAppFilterFlag);
    checkAppFilterFlag();

    return () => window.removeEventListener('storage', checkAppFilterFlag);
  }, []);

  const handleMenuOpen = key => {
    clearTimeout(closeTimeout.current);
    setActiveSubmenu(key);
    setIsSubmenuOpen(true);
  };

  const handleMenuClose = () => {
    closeTimeout.current = setTimeout(() => {
      setIsSubmenuOpen(false);
      setActiveSubmenu(null);
    }, 150);
  };

  const handleSubmenuHover = () => {
    clearTimeout(closeTimeout.current);
    setIsSubmenuOpen(true);
  };

  const handleRightClickOnLogo = e => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleDownloadSvg = () => {
    // Use the imported SVG file directly
    const svgUrl = AlloyAutomationLogo;

    if (svgUrl) {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = svgUrl;
      link.download = 'alloy-automation-logo.svg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowContextMenu(false);
    }
  };

  useEffect(() => {
    if (showContextMenu) {
      const handleClickOutside = event => {
        // Only close if clicking outside the context menu
        if (!event.target.closest('.logo-context-menu')) {
          setShowContextMenu(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showContextMenu]);

  const getStartedBtn = (
    <a
      className="flex h-full items-center justify-center bg-jade-500 px-4 py-2 text-sm font-medium text-white transition-smooth hover:bg-jade-800 hover:!text-white"
      href={`/book-demo/${getUtmSource()}`}
    >
      Book a demo
    </a>
  );

  const loginBtn = (
    <APopover
      placement="bottom"
      panelClassName="w-[100px] [--anchor-gap:4px] "
      content={
        <>
          <a
            href="https://app.runalloy.com/login"
            className="flex items-center justify-center gap-2 px-3 py-1 text-sm font-medium text-black transition-smooth hover:bg-azure-100"
          >
            US <img src={USFlag} alt="us-flag" />
          </a>
          <a
            href="https://eu-app.runalloy.com/login"
            className="flex items-center justify-center gap-2 px-3 py-1 text-sm font-medium text-black transition-smooth hover:bg-azure-100"
          >
            EU <img src={EUFlag} alt="us-flag" />
          </a>
        </>
      }
      showIcon
    >
      <span className="border-r border-r-black/10 pr-3 text-sm !font-medium text-black">
        Login
      </span>
    </APopover>
  );

  return (
    <>
      <Helmet>
        <html lang={`en`} />
        <title>
          {metaTitle
            ? metaTitle
            : `Alloy Automation | The Integration Infrastructure for the Age of AI`}
        </title>
        <meta
          name="title"
          content={
            metaTitle
              ? metaTitle
              : `Alloy Automation | The Integration Infrastructure for the Age of AI`
          }
          data-react-helmet="true"
        />
        <meta
          name="description"
          content={
            metaDescription
              ? metaDescription
              : 'From enabling secure autonomous AI agents to powering your product’s integration marketplace, we help you build faster and enhance your product with one enterprise-grade integration platform.'
          }
          data-react-helmet="true"
        />
        <meta
          name="keywords"
          content={
            metaKeywords
              ? metaKeywords
              : `shopify plus, shopify flow, parabola.io, ecomm automation, shopify integration, shopify automation, automate shopify, automate shopify store, alloy jobs, alloy automation recruiting, alloy automation engineer, alloy automation software engineer`
          }
          data-react-helmet="true"
        />
        <meta
          property="og:url"
          content={defaultOGUrl}
          data-react-helmet="true"
        />
        <meta
          property="og:type"
          content={article ? 'article' : 'website'}
          data-react-helmet="true"
        />
        <meta
          property="og:title"
          content={
            metaTitle
              ? metaTitle
              : `Alloy Automation | The Integration Infrastructure for the Age of AI`
          }
          data-react-helmet="true"
        />
        <meta
          property="og:description"
          content={metaDescription ? metaDescription : 'iPass Platform'}
          data-react-helmet="true"
        />
        <meta
          property="og:image"
          content={seoImage ? seoImage : defaultOGImage}
          data-react-helmet="true"
        />
        <meta name="twitter:card" content="summary_large_image" />
        {twitterUsername && (
          <meta
            name="twitter:creator"
            content={twitterUsername}
            data-react-helmet="true"
          />
        )}
        <meta
          property="twitter:title"
          content={
            metaTitle
              ? metaTitle
              : `Alloy Automation | The Integration Infrastructure for the Age of AI`
          }
          data-react-helmet="true"
        />
        <meta
          property="twitter:description"
          content={metaDescription ? metaDescription : 'iPaaS Platform'}
          data-react-helmet="true"
        />
        <meta
          property="twitter:image"
          content={seoImage ? seoImage : defaultOGImage}
          data-react-helmet="true"
        />
        <meta
          property="twitter:image:src"
          content={seoImage ? seoImage : defaultOGImage}
          data-react-helmet="true"
        />
        <meta
          name="facebook-domain-verification"
          content="4og7d4dybt43c6ogmncx13y1ouox3x"
        />
        <meta
          name="google-site-verification"
          content="_JYH63RVRh693zcjkjG09GXHi7uAkrIpT5QKgX1DYrM"
        />
        <meta name="referrer" content="unsafe-url" />

        <script src="https://ajax.googleapis.com/ajax/libs/jquery/3.5.1/jquery.min.js"></script>
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=AW-428218845"
        ></script>
        <script>
          {`window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'AW-428218845');`}
        </script>
        <script>
          {`(function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:2607974,hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}
        </script>
        <script>
          {`!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=1;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '271645134870415');
            fbq('track', 'PageView');`}
        </script>
        <noscript>
          {`<img
              height="1"
              width="1"
              style="display:none"
              src="https://www.facebook.com/tr?id=271645134870415&ev=PageView&noscript=1"
            />`}
        </noscript>
        <script
          src="https://e1caac3560204f10a0e7c0766b42359c.js.ubembed.com"
          async
          defer
        ></script>
        <script
          src="https://accounts.google.com/gsi/client"
          async
          defer
        ></script>
        <script
          type="text/javascript"
          id="hs-script-loader"
          async
          defer
          src="//js.hs-scripts.com/6359009.js"
        ></script>
        <script type="text/javascript">
          {`_linkedin_partner_id = "7702466";
            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(_linkedin_partner_id);`}
        </script>
        <script type="text/javascript">
          {`(function(l) {
            if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
            window.lintrk.q=[]}
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript";b.async = true;
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);})(window.lintrk);`}
        </script>
        <noscript>
          {`<img
              height="1"
              width="1"
              style="display:none;"
              alt=""
              src="https://px.ads.linkedin.com/collect/?pid=7702466&fmt=gif"
            />`}
        </noscript>
        <script>{`(function(a,b,c,d){window.fetch("https://www.g2.com/products/alloy-alloy-automation/rating_schema.json").then(e=>e.json()).then(f=>{c=a.createElement(b);c.type="application/ld+json";c.text=JSON.stringify(f);d=a.getElementsByTagName(b)[0];d.parentNode.insertBefore(c,d);});})(document,"script");`}</script>
        <script>
          {`(function (c, p, d, u, i) {
              u =
                'https://tracking.g2crowd.com/funnels/' +
                c +
                '.js?stage=finish&stype=offer';
              i = document.createElement('script');
              i.type = 'application/javascript';
              i.async = true;
              i.src = u;
              d.getElementsByTagName('head')[0].appendChild(i);
            })('88d588d5-bb6c-437c-98b5-d49170ea124e', document.location.href, document);`}
        </script>
        <script>
          {`!function() {
              var reb2b = window.reb2b = window.reb2b || [];
              if (reb2b.invoked) return;
              reb2b.invoked = true;
              reb2b.methods = ["identify", "collect"];
              reb2b.factory = function(method) {
                return function() {
                  var args = Array.prototype.slice.call(arguments);
                  args.unshift(method);
                  reb2b.push(args);
                  return reb2b;
                };
              };
              for (var i = 0; i < reb2b.methods.length; i++) {
                var key = reb2b.methods[i];
                reb2b[key] = reb2b.factory(key);
              }
              reb2b.load = function(key) {
                var script = document.createElement("script");
                script.type = "text/javascript";
                script.async = true;
                script.src = "https://s3-us-west-2.amazonaws.com/b2bjsstore/b/" + key + "/DNXY8HXKR8O0.js.gz";
                var first = document.getElementsByTagName("script")[0];
                first.parentNode.insertBefore(script, first);
              };
              reb2b.SNIPPET_VERSION = "1.0.1";
              reb2b.load("DNXY8HXKR8O0");
            }();`}
        </script>
        <script
          id="warmly-script-loader"
          src="https://opps-widget.getwarmly.com/warmly.js?clientId=92b237d92f437ac97e8f6fe5eb633726"
          defer
        ></script>
        <script src="https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.4.25/dist/unicornStudio.umd.js"></script>
        <link rel="icon" type="image/png" href="/favicon.png" sizes="32x32" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      </Helmet>
      <div className="layout">
        <Suspense fallback={<div />}>
          <LazyGdpr location={location} />
        </Suspense>
        <header
          className={classNames(
            'fixed z-[1100] w-full border-b border-b-black/10 bg-white backdrop-blur will-change-transform transition-smooth',
            {
              '-translate-y-12 sm:translate-y-0': !loading,
              '-translate-y-full': loading,
              'z-[998]': isAppFilterOverlayOpen,
            }
          )}
        >
          {hasBanner && !flag && (
            <Suspense fallback={<div />}>
              <LazyBanner />
            </Suspense>
          )}
          <div className="relative mx-auto h-12 bg-white sm:p-0.5">
            <div className="hidden h-full items-center justify-between bg-white lg:flex">
              <div className="flex h-full items-center bg-white">
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  ref={logoRef}
                  onContextMenu={handleRightClickOnLogo}
                  onKeyDown={e => {
                    // Allow keyboard users to access the context menu with Shift+F10 or the context menu key
                    if (
                      (e.key === 'F10' && e.shiftKey) ||
                      e.key === 'ContextMenu'
                    ) {
                      e.preventDefault();
                      const rect = logoRef.current.getBoundingClientRect();
                      setContextMenuPosition({
                        x: rect.left,
                        y: rect.bottom,
                      });
                      setShowContextMenu(true);
                    }
                  }}
                  tabIndex="0"
                  role="button"
                  aria-label="Right click for options"
                >
                  <Link
                    to="/"
                    className="flex h-full items-center border-r border-r-black/10 px-4 outline-none dark:border-r-white/10 sm:px-5"
                  >
                    <ALogo
                      className="h-7 w-auto !gap-0.5"
                      textClassName="!h-[calc(100%-4px)]"
                    />
                  </Link>
                </div>
                {!hideMenu && (
                  <nav className="flex h-full items-center">
                    {/* Menu items */}
                    <SubmenuButton
                      isOpen={activeSubmenu === 'platform'}
                      onHover={() => handleMenuOpen('platform')}
                      onLeave={handleMenuClose}
                    >
                      Platform
                    </SubmenuButton>
                    <SubmenuButton
                      isOpen={activeSubmenu === 'solutions'}
                      onHover={() => handleMenuOpen('solutions')}
                      onLeave={handleMenuClose}
                    >
                      Solutions
                    </SubmenuButton>
                    <SubmenuButton
                      isOpen={activeSubmenu === 'resources'}
                      onHover={() => handleMenuOpen('resources')}
                      onLeave={handleMenuClose}
                    >
                      Resources
                    </SubmenuButton>
                    <SubmenuButton
                      isOpen={activeSubmenu === 'case-studies'}
                      onHover={() => handleMenuOpen('case-studies')}
                      onLeave={handleMenuClose}
                    >
                      Case Studies
                    </SubmenuButton>
                    <SubmenuLink link="/partners/become-a-partner/">
                      Partners
                    </SubmenuLink>
                    {/* <SubmenuButton
                      isOpen={activeSubmenu === 'partners'}
                      onHover={() => handleMenuOpen('partners')}
                      onLeave={handleMenuClose}
                    >
                      Partners
                    </SubmenuButton> */}

                    {/* Flyout menu */}
                    <SubmenuFlyoutMenu
                      isOpen={isSubmenuOpen && activeSubmenu}
                      onHover={handleSubmenuHover}
                      onLeave={handleMenuClose}
                    >
                      {activeSubmenu === 'platform' && <PlatformContent />}
                      {activeSubmenu === 'solutions' && <SolutionsContent />}
                      {activeSubmenu === 'resources' && (
                        <ResourcesContent blogs={blogSample} />
                      )}
                      {activeSubmenu === 'case-studies' && (
                        <CaseStudiesContent />
                      )}
                      {activeSubmenu === 'partners' && <PartnersContent />}
                    </SubmenuFlyoutMenu>
                  </nav>
                )}
              </div>
              <div className="flex h-full items-center bg-white">
                {!hideLoginBtn && loginBtn}
                {getStartedBtn}
              </div>
            </div>
            <div className="flex items-center justify-between lg:hidden">
              <Link
                to="/"
                className="flex h-full items-center p-3 outline-none"
              >
                <ALogo
                  className="h-6 w-auto !gap-0.5"
                  textClassName="!h-full"
                />
              </Link>
              <div className="h-12 w-12">
                <Hamburger
                  size={22}
                  direction="right"
                  color="#2B2E32"
                  onToggle={toggled => {
                    if (toggled) {
                      setMobileMenuVisible(true);
                      // document.body.style.overflow = 'hidden';
                    } else {
                      setMobileMenuVisible(false);
                      // document.body.style.overflow = 'unset';
                    }
                  }}
                  rounded
                />
              </div>
              <MobileDrawer
                visible={mobileMenuVisible}
                blogs={blogSample}
                login={`${getAppUrl()}/login`}
              />
            </div>
          </div>
          {showContextMenu && (
            <LogoContextMenu
              x={contextMenuPosition.x}
              y={contextMenuPosition.y}
              onDownloadSvg={handleDownloadSvg}
            />
          )}
        </header>
        {!loading && (
          <>
            <div
              className={classNames('mt-12 text-base text-black sm:mt-0', {
                'mt-12 pt-12 sm:mt-0 [&_.sectionPositionedAsSticky]:!top-[96px]':
                  hasBanner && !flag,
              })}
              ref={pageRef || null}
            >
              {children}
            </div>
            <SiteFooter
              screenSize={screenSize}
              hideFooterMenu={hideFooterMenu}
            />
          </>
        )}
        <Toaster
          toastOptions={{
            className:
              'text-sm text-black !max-w-max gap-2 [&>div:last-child]:mx-0',
            duration: 5000,
          }}
        />
      </div>
    </>
  );
};

SiteLayout.propTypes = {
  children: PropTypes.node.isRequired,
  hideMenu: PropTypes.bool,
  hideFooterMenu: PropTypes.bool,
  hideLoginBtn: PropTypes.bool,
  metaTitle: PropTypes.string,
  metaKeywords: PropTypes.string,
  metaDescription: PropTypes.string,
};

const DefaultLayout = props => {
  const data = useStaticQuery(graphql`
    query {
      strapiHomePage {
        seo_image {
          url
        }
      }
      strapiBanner {
        show_banner
      }
      allStrapiBlog(limit: 3, sort: { date_published: DESC }) {
        edges {
          node {
            ...BlogFields
          }
        }
      }
    }
  `);

  return <SiteLayout data={data} {...props} />;
};

export default DefaultLayout;
